import React, { useMemo, useState, useCallback } from 'react';
import { CheckCircle2, Clock, ChevronRight, ChevronDown, GitBranch, SkipForward, AlertCircle } from 'lucide-react';
import { Button, StatusBadge, ICON_PX } from '../ui';
import CompletionForm from './CompletionForm';

/**
 * Smart linearized execution view of a DAG workflow.
 * Tasks expand inline (accordion) to show completion form.
 * Only one task can be expanded at a time.
 */
function WorkflowExecutionList({ workflow, nodeStates = {}, onCompleteTask, onSkipTask, onGateSelect }) {
  const [expandedId, setExpandedId] = useState(null);
  const [completedExpanded, setCompletedExpanded] = useState(false);

  const toggleExpand = useCallback((nodeId) => {
    setExpandedId((prev) => (prev === nodeId ? null : nodeId));
  }, []);

  // Topological sort
  const sortedNodes = useMemo(() => {
    if (!workflow?.nodes || !workflow?.edges) return [];
    const nodes = workflow.nodes;
    const edges = workflow.edges;
    const inDegree = {};
    const adj = {};
    nodes.forEach((n) => { inDegree[n.id] = 0; adj[n.id] = []; });
    edges.forEach((e) => { adj[e.from]?.push(e.to); inDegree[e.to] = (inDegree[e.to] || 0) + 1; });
    const queue = nodes.filter((n) => inDegree[n.id] === 0).map((n) => n.id);
    const sorted = [];
    while (queue.length > 0) {
      const id = queue.shift();
      const node = nodes.find((n) => n.id === id);
      if (node) sorted.push(node);
      (adj[id] || []).forEach((to) => {
        inDegree[to]--;
        if (inDegree[to] === 0) queue.push(to);
      });
    }
    return sorted;
  }, [workflow]);

  // Step numbers (task nodes only)
  const stepNumbers = useMemo(() => {
    const nums = {};
    let step = 1;
    sortedNodes.forEach((n) => {
      if (n.type === 'task') nums[n.id] = step++;
    });
    return nums;
  }, [sortedNodes]);

  // Separate completed tasks (collapsible group at top) from active/pending
  const { completedItems, activeItems } = useMemo(() => {
    const completed = [];
    const active = [];

    for (const node of sortedNodes) {
      const state = nodeStates[node.id];
      const status = state?.status || 'pending';

      if (node.type === 'join') continue;

      if (status === 'satisfied' && node.type !== 'end') {
        completed.push({ ...node, state });
      } else if (node.type === 'end') {
        active.push({ type: 'end', node, status });
      } else {
        active.push({
          type: node.type === 'gate' && status === 'available' ? 'gate' : status,
          node,
          status,
          state,
        });
      }
    }
    return { completedItems: completed, activeItems: active };
  }, [sortedNodes, nodeStates]);

  // Get parent labels for "waiting on" display
  const getParentLabels = (nodeId) => {
    if (!workflow?.edges) return [];
    const parentIds = workflow.edges.filter((e) => e.to === nodeId).map((e) => e.from);
    return parentIds
      .filter((pid) => nodeStates[pid]?.status !== 'satisfied' && nodeStates[pid]?.status !== 'skipped')
      .map((pid) => workflow.nodes.find((n) => n.id === pid)?.label || pid);
  };

  if (completedItems.length === 0 && activeItems.length === 0) {
    return <div className="p-4 text-center text-slate-400 text-sm">No workflow selected</div>;
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="divide-y divide-slate-100">
        {/* ── Completed tasks accordion ── */}
        {completedItems.length > 0 && (
          <div>
            <button
              type="button"
              onClick={() => setCompletedExpanded((prev) => !prev)}
              className="w-full px-4 py-3 bg-[var(--green)]/5 hover:bg-[var(--green)]/10 transition-colors flex items-center gap-2.5 text-left"
            >
              <CheckCircle2 size={ICON_PX.md} className="text-green-nn shrink-0" />
              <span className="text-sm text-green-nn font-medium flex-1">
                {completedItems.length} completed {completedItems.length === 1 ? 'step' : 'steps'}
              </span>
              {completedExpanded
                ? <ChevronDown size={ICON_PX.sm} className="text-green-nn shrink-0" />
                : <ChevronRight size={ICON_PX.sm} className="text-green-nn shrink-0" />
              }
            </button>
            {completedExpanded && (
              <div className="divide-y divide-[var(--green)]/10">
                {completedItems.map((n) => {
                  const step = stepNumbers[n.id];
                  return (
                    <div key={n.id} className="px-4 py-2.5 bg-[var(--green)]/3">
                      <div className="flex items-center gap-2.5 ml-[26px]">
                        <div className="flex-1 min-w-0">
                          {step && <span className="text-[10px] font-semibold text-green-nn uppercase tracking-wide mr-2">Step {step}</span>}
                          <span className="text-sm text-text-dark">{n.label}</span>
                        </div>
                        {n.state?.satisfiedBy && (
                          <span className="text-xs text-slate-400 shrink-0 truncate max-w-[120px]">
                            {n.state.satisfiedBy}
                          </span>
                        )}
                      </div>
                      {n.state?.satisfiedAt && (
                        <div className="text-[10px] text-slate-400 mt-0.5 ml-[26px]">
                          {new Date(n.state.satisfiedAt).toLocaleString()}
                          {n.state.completionSource && ` · via ${n.state.completionSource}`}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Active / pending items ── */}
        {activeItems.map((item, i) => {
          // ── Available task (expandable with completion form) ──
          if (item.type === 'available' && item.node.type === 'task') {
            const step = stepNumbers[item.node.id];
            const isExpanded = expandedId === item.node.id;
            return (
              <div key={item.node.id}>
                <div
                  className="px-4 py-4 bg-[var(--aqua)]/5 border-l-[3px] border-l-[var(--aqua)] cursor-pointer hover:bg-[var(--aqua)]/10 transition-colors"
                  onClick={() => toggleExpand(item.node.id)}
                >
                  <div className="flex items-center gap-2.5">
                    <div className="w-4 h-4 rounded-full border-[2.5px] border-[var(--aqua)] shrink-0" />
                    <div className="flex-1 min-w-0">
                      {step && <span className="text-[10px] font-semibold text-[var(--aqua)] uppercase tracking-wide mr-2">Step {step}</span>}
                      <span className="text-sm font-medium text-text-dark">{item.node.label}</span>
                    </div>
                    {isExpanded ? <ChevronDown size={ICON_PX.sm} className="text-[var(--aqua)] shrink-0" /> : <ChevronRight size={ICON_PX.sm} className="text-[var(--aqua)] shrink-0" />}
                  </div>
                  {!isExpanded && item.node.completionType && (
                    <div className="text-xs text-slate-400 mt-1 ml-[26px]">
                      {item.node.completionType === 'checkbox_note' ? 'Add a note' :
                       item.node.completionType === 'checkbox_linked' ? 'Attach evidence' : 'Check off'}
                    </div>
                  )}
                </div>
                {/* Expanded: inline completion form */}
                {isExpanded && (
                  <div className="px-4 py-4 bg-[var(--aqua)]/3 border-l-[3px] border-l-[var(--aqua)] border-t border-[var(--aqua)]/10">
                    <div className="ml-[26px] space-y-3">
                      <div className="flex items-start gap-2 rounded-lg border border-[var(--aqua)]/20 bg-white p-2.5">
                        <AlertCircle size={ICON_PX.sm} className="text-[var(--aqua)] shrink-0 mt-0.5" />
                        <p className="text-xs text-slate-500">
                          Complete this step here, or from the Priority Queue — it updates everywhere.
                        </p>
                      </div>
                      <CompletionForm
                        completionType={item.node.completionType || 'checkbox'}
                        compact
                        onSubmit={(data) => {
                          onCompleteTask?.(item.node, data);
                          setExpandedId(null);
                        }}
                      />
                      {/* Skip option */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSkipTask?.(item.node);
                          setExpandedId(null);
                        }}
                        className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors"
                      >
                        <SkipForward size={12} />
                        Skip this step
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          }

          // ── Gate (decision — inline path buttons) ──
          if (item.type === 'gate') {
            return (
              <div key={item.node.id} className="px-4 py-4 bg-[var(--aqua)]/5 border-l-[3px] border-l-[var(--aqua)]">
                <div className="flex items-center gap-2.5 mb-2.5">
                  <GitBranch size={ICON_PX.md} className="text-[var(--aqua)] shrink-0" />
                  <span className="text-sm font-medium text-text-dark">{item.node.label || 'Decision'}</span>
                </div>
                <div className="flex gap-2 ml-[26px]">
                  {(item.node.options || ['Yes', 'No']).map((opt) => (
                    <Button key={opt} variant="outline" size="sm" onClick={() => onGateSelect?.(item.node.id, opt)}>
                      {opt}
                    </Button>
                  ))}
                </div>
              </div>
            );
          }

          // ── Blocked ──
          if (item.type === 'blocked') {
            const waitingOn = getParentLabels(item.node.id);
            const step = stepNumbers[item.node.id];
            return (
              <div key={item.node.id} className="px-4 py-3 opacity-50">
                <div className="flex items-center gap-2.5">
                  <Clock size={ICON_PX.md} className="text-slate-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    {step && <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mr-2">Step {step}</span>}
                    <span className="text-sm text-slate-500">{item.node.label}</span>
                  </div>
                </div>
                {waitingOn.length > 0 && (
                  <div className="text-xs text-slate-400 mt-0.5 ml-[26px]">
                    Waiting on: {waitingOn.join(', ')}
                  </div>
                )}
              </div>
            );
          }

          // ── End ──
          if (item.type === 'end') {
            const isComplete = item.status === 'satisfied';
            return (
              <div key={item.node.id} className={`px-4 py-3 ${isComplete ? 'bg-[var(--green)] text-white' : ''}`}>
                <div className="flex items-center gap-2.5">
                  {isComplete ? (
                    <CheckCircle2 size={ICON_PX.md} className="text-white" />
                  ) : (
                    <Clock size={ICON_PX.md} className="text-slate-400" />
                  )}
                  <span className={`text-sm font-semibold ${isComplete ? 'text-white' : 'text-slate-400'}`}>
                    {isComplete ? 'All Done!' : (item.node.label || 'Finish')}
                  </span>
                </div>
              </div>
            );
          }

          // ── Skipped fallback ──
          if (item.node) {
            return (
              <div key={item.node.id} className="px-4 py-3 opacity-30">
                <div className="flex items-center gap-2.5">
                  <span className="text-sm text-slate-400 line-through">{item.node.label}</span>
                  <StatusBadge variant="watch">Skipped</StatusBadge>
                </div>
              </div>
            );
          }

          return null;
        })}
      </div>
    </div>
  );
}

export default WorkflowExecutionList;
